import { Suspense, useRef, useState, type ReactNode } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { Sidebar } from '../components/layout/Sidebar';
import { TopBar } from '../components/layout/TopBar';
import { MobileHeader } from '../components/layout/MobileHeader';
import { SubscriptionBanner } from '../components/layout/SubscriptionBanner';
import { AdministeredEntityBanner } from '../components/layout/AdministeredEntityBanner';
import { useAuth } from '../lib/auth/useAuth';
import { useTimer } from '../lib/timer/TimerContext';
import { TimerProvider } from '../lib/timer/TimerProvider';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { formatElapsedCompact } from '../components/timer/format-elapsed';
import { useSubscription } from '../lib/subscription/useSubscription';
import { RouteErrorBoundary } from '../components/RouteErrorBoundary';
import { routeBoundaryKey } from '../lib/route-boundary-key';
import { LoadingState } from '../components/ui/LoadingState';
import { ActivityComposerProvider } from '../components/activity/ActivityComposerProvider';
import { UiUsageRecorder } from '../lib/ui-usage';
import { useReferralClaim } from '../hooks/useReferralClaim';
import { useScrollerRestoration } from '../hooks/useScrollerRestoration';

/**
 * Layout for authenticated routes.
 * Auth protection is handled by RouteGuard in App.tsx.
 * This component can assume the user is authenticated.
 *
 * `routes` is the authenticated route table the page-view recorder matches against.
 */
export function Layout({ routes }: { routes: ReactNode }) {
  const { accountId } = useAuth();
  return (
    // Keyed on the account so a switch remounts everything below with the new
    // account's state: the timer and the dashboard chat read their account's
    // stored state only on mount.
    <TimerProvider key={accountId}>
      <TimerDocumentTitle />
      <UiUsageRecorder routes={routes} />
      <ActivityComposerProvider>
        <LayoutInner />
      </ActivityComposerProvider>
    </TimerProvider>
  );
}

/**
 * Isolated component for the dynamic tab title so the 1/s elapsed re-renders
 * don't propagate through the entire layout tree.
 */
function TimerDocumentTitle() {
  const { hasTimer, isPaused, elapsed } = useTimer();
  const title = hasTimer
    ? `${isPaused ? '\u23f8' : '\u23f1'} ${formatElapsedCompact(elapsed)}${isPaused ? ' paused' : ''} - Bill`
    : null;
  useDocumentTitle(title);
  return null;
}

function LayoutInner() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { needsOnboarding } = useSubscription();
  const location = useLocation();
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLElement>(null);
  const rememberScroll = useScrollerRestoration(scrollerRef);
  useReferralClaim();

  // New account with no subscription history - send back to complete onboarding
  if (needsOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  return (
    /* h-dvh, not h-screen: vh resolves against the large viewport (the height
       with the mobile browser toolbar retracted), so a 100vh shell hides its
       bottom ~60px behind the toolbar whenever the toolbar is showing. dvh
       tracks the height actually visible. Desktop browsers have no retractable
       chrome, so the two units are identical there - which is why this class of
       bug never reproduces in DevTools device emulation or Playwright. */
    <div className="flex h-dvh flex-col">
      {/* The phone header stays above the drawer/content row. */}
      <MobileHeader onMenuClick={() => setIsMobileMenuOpen(true)} />

      <div className="flex min-h-0 flex-1">
        {/* Desktop sidebar - hidden on mobile */}
        <Sidebar className="hidden lg:flex" />

        {/* Mobile sidebar overlay */}
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-40 bg-black/50 lg:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            {/* Sidebar drawer */}
            <Sidebar
              className="fixed inset-y-0 left-0 z-50 lg:hidden"
              onClose={() => setIsMobileMenuOpen(false)}
            />
          </>
        )}

        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-bg-subtle">
          <AdministeredEntityBanner />
          <SubscriptionBanner />
          <div ref={contentRef} className="group/content relative flex min-h-0 flex-1 flex-col">
            <TopBar />
            {/*
              The scroll container spans the full width so the wheel works anywhere in the
              viewport and the scrollbar sits against the window edge. The page-width cap
              lives on the inner wrapper instead.

              main is a single-column grid, not a flex column, for two reasons:
              - The wrapper is stretched by the grid, giving it a definite height, so routes
                that fill the viewport (`PageShell layout="fill"`) can size against it. A flex-1
                wrapper leaves the height indefinite and a percentage height collapses to the
                content height.
              - Route content stays in normal block flow inside the wrapper. Route roots are
                mx-auto with no w-full, so as flex or grid items they would shrink to their
                content width - cross-axis auto margins beat stretch.
              grid-cols-1 is minmax(0, 1fr), which keeps wide content from blowing the track out.
            */}
            {/* Written as a data attribute so the TopBar backdrop fades without re-rendering the route. */}
            <main
              ref={scrollerRef}
              onScroll={(e) => {
                if (contentRef.current) contentRef.current.dataset.scrolled = String(e.currentTarget.scrollTop > 0);
                rememberScroll(e.currentTarget.scrollTop);
              }}
              className="grid min-h-0 w-full flex-1 grid-cols-1 overflow-auto lg:scroll-pt-14 [scrollbar-gutter:stable]"
            >
              {/* Reserve initial space for the floating controls inside the scroller. */}
              <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8 lg:pb-8 lg:pt-18">
                <RouteErrorBoundary key={routeBoundaryKey(location.pathname)} resetKey={location.pathname}>
                  {/* Keeps the sidebar and top bar in place while a page's code loads. */}
                  <Suspense fallback={<LoadingState message="Loading..." />}>
                    <Outlet />
                  </Suspense>
                </RouteErrorBoundary>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
}
